// Gantt export utilities
import { GanttData, GanttConfig, GanttFilters } from '../types/gantt.types';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Export to PDF
export const exportGanttToPDF = async (
  data: GanttData,
  config: GanttConfig,
  filters: GanttFilters
): Promise<void> => {
  try {
    // Get the Gantt chart element
    const ganttElement = document.querySelector('.gantt-timeline');
    if (!ganttElement) {
      throw new Error('Gantt chart element not found');
    }

    // Convert to canvas
    const canvas = await html2canvas(ganttElement as HTMLElement, {
      backgroundColor: '#18181b',
      scale: 2
    });

    // Create PDF
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a3'
    });

    // Add title
    pdf.setFontSize(20);
    pdf.text('Wykres Gantta - PlexiSystem', 20, 20);

    // Add metadata
    pdf.setFontSize(12);
    const date = new Date().toLocaleDateString('pl-PL');
    pdf.text(`Data eksportu: ${date}`, 20, 30);
    pdf.text(`Liczba zadań: ${data.tasks.length}`, 20, 35);
    pdf.text(`Liczba projektów: ${data.projects.length}`, 20, 40);

    // Add chart image
    const imgData = canvas.toDataURL('image/png');
    const imgWidth = 380;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    pdf.addImage(imgData, 'PNG', 10, 50, imgWidth, imgHeight);

    // Add task list on new page
    pdf.addPage();
    pdf.setFontSize(16);
    pdf.text('Lista zadań', 20, 20);

    let yPosition = 30;
    pdf.setFontSize(10);

    data.tasks.forEach((task, index) => {
      if (yPosition > 270) {
        pdf.addPage();
        yPosition = 20;
      }

      const status = task.status === 'completed' ? '✓' : 
                    task.status === 'in_progress' ? '→' : 
                    task.status === 'delayed' ? '!' : '○';

      pdf.text(`${index + 1}. ${status} ${task.name}`, 20, yPosition);
      pdf.text(`Termin: ${task.start_date.toLocaleDateString('pl-PL')} - ${task.end_date.toLocaleDateString('pl-PL')}`, 30, yPosition + 5);
      pdf.text(`Postęp: ${task.progress}%`, 30, yPosition + 10);
      
      yPosition += 15;
    });

    // Save PDF
    pdf.save(`gantt-chart-${Date.now()}.pdf`);
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    throw error;
  }
};

// Export to Excel
export const exportGanttToExcel = async (
  data: GanttData,
  config: GanttConfig,
  filters: GanttFilters
): Promise<void> => {
  try {
    // Create workbook
    const wb = XLSX.utils.book_new();

    // Projects sheet
    const projectsData = data.projects.map(project => ({
      'ID': project.id,
      'Nazwa': project.name,
      'Klient': project.client_name || '',
      'Data rozpoczęcia': project.start_date.toLocaleDateString('pl-PL'),
      'Data zakończenia': project.end_date.toLocaleDateString('pl-PL'),
      'Status': project.status,
      'Budżet': project.budget || 0,
      'Wydano': project.spent || 0,
      'Postęp (%)': project.progress
    }));

    const projectsSheet = XLSX.utils.json_to_sheet(projectsData);
    XLSX.utils.book_append_sheet(wb, projectsSheet, 'Projekty');

    // Tasks sheet
    const tasksData = data.tasks.map(task => ({
      'ID': task.id,
      'Projekt': data.projects.find(p => p.id === task.project_id)?.name || '',
      'Nazwa zadania': task.name,
      'Opis': task.description || '',
      'Data rozpoczęcia': task.start_date.toLocaleDateString('pl-PL'),
      'Data zakończenia': task.end_date.toLocaleDateString('pl-PL'),
      'Czas trwania (dni)': task.duration,
      'Postęp (%)': task.progress,
      'Status': task.status,
      'Priorytet': task.priority,
      'Przypisano do': task.assigned_users?.map(u => u.name).join(', ') || '',
      'Szacowany czas (h)': task.estimated_hours || 0,
      'Ścieżka krytyczna': task.is_critical_path ? 'Tak' : 'Nie'
    }));

    const tasksSheet = XLSX.utils.json_to_sheet(tasksData);
    
    // Set column widths
    const tasksCols = [
      { wch: 10 }, // ID
      { wch: 25 }, // Projekt
      { wch: 30 }, // Nazwa zadania
      { wch: 40 }, // Opis
      { wch: 15 }, // Data rozpoczęcia
      { wch: 15 }, // Data zakończenia
      { wch: 15 }, // Czas trwania
      { wch: 10 }, // Postęp
      { wch: 12 }, // Status
      { wch: 10 }, // Priorytet
      { wch: 25 }, // Przypisano do
      { wch: 15 }, // Szacowany czas
      { wch: 15 }  // Ścieżka krytyczna
    ];
    tasksSheet['!cols'] = tasksCols;

    XLSX.utils.book_append_sheet(wb, tasksSheet, 'Zadania');

    // Resources sheet
    const resourcesData: any[] = [];

    // Add users
    data.users.forEach(user => {
      const userTasks = data.tasks.filter(t => t.assigned_to?.includes(user.id));
      const totalHours = userTasks.reduce((sum, t) => sum + (t.estimated_hours || t.duration * 8), 0);
      
      resourcesData.push({
        'Typ': 'Pracownik',
        'Nazwa': user.name,
        'Email': user.email,
        'Rola': user.role || '',
        'Liczba zadań': userTasks.length,
        'Łączny czas (h)': totalHours,
        'Wydajność (h/dzień)': user.capacity || 8
      });
    });

    // Add machines
    data.machines.forEach(machine => {
      resourcesData.push({
        'Typ': 'Maszyna',
        'Nazwa': machine.name,
        'Email': '-',
        'Rola': machine.type,
        'Liczba zadań': 0,
        'Łączny czas (h)': 0,
        'Wydajność (h/dzień)': machine.capacity,
        'Status': machine.status
      });
    });

    if (resourcesData.length > 0) {
      const resourcesSheet = XLSX.utils.json_to_sheet(resourcesData);
      XLSX.utils.book_append_sheet(wb, resourcesSheet, 'Zasoby');
    }

    // Dependencies sheet
    const dependenciesData: any[] = [];
    data.tasks.forEach(task => {
      task.dependencies?.forEach(dep => {
        const fromTask = data.tasks.find(t => t.id === dep.task_id);
        const toTask = data.tasks.find(t => t.id === dep.dependent_task_id);
        
        dependenciesData.push({
          'Od zadania': fromTask?.name || dep.task_id,
          'Do zadania': toTask?.name || dep.dependent_task_id,
          'Typ': dep.type,
          'Opóźnienie (dni)': dep.lag || 0
        });
      });
    });

    if (dependenciesData.length > 0) {
      const dependenciesSheet = XLSX.utils.json_to_sheet(dependenciesData);
      XLSX.utils.book_append_sheet(wb, dependenciesSheet, 'Zależności');
    }

    // Statistics sheet
    const statsData = [
      { 'Metryka': 'Całkowita liczba zadań', 'Wartość': data.tasks.length },
      { 'Metryka': 'Zadania ukończone', 'Wartość': data.tasks.filter(t => t.status === 'completed').length },
      { 'Metryka': 'Zadania w trakcie', 'Wartość': data.tasks.filter(t => t.status === 'in_progress').length },
      { 'Metryka': 'Zadania opóźnione', 'Wartość': data.tasks.filter(t => t.status === 'delayed').length },
      { 'Metryka': 'Zadania na ścieżce krytycznej', 'Wartość': data.tasks.filter(t => t.is_critical_path).length },
      { 'Metryka': 'Średni postęp (%)', 'Wartość': Math.round(data.tasks.reduce((sum, t) => sum + t.progress, 0) / data.tasks.length) || 0 },
      { 'Metryka': 'Liczba projektów', 'Wartość': data.projects.length },
      { 'Metryka': 'Liczba użytkowników', 'Wartość': data.users.length },
      { 'Metryka': 'Liczba maszyn', 'Wartość': data.machines.length }
    ];

    const statsSheet = XLSX.utils.json_to_sheet(statsData);
    XLSX.utils.book_append_sheet(wb, statsSheet, 'Statystyki');

    // Save file
    XLSX.writeFile(wb, `gantt-export-${Date.now()}.xlsx`);
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    throw error;
  }
};

// Export to CSV
export const exportGanttToCSV = (data: GanttData): void => {
  const csvData: string[] = [];
  
  // Header
  csvData.push('ID,Projekt,Zadanie,Data rozpoczęcia,Data zakończenia,Czas trwania,Postęp,Status,Priorytet');
  
  // Tasks
  data.tasks.forEach(task => {
    const project = data.projects.find(p => p.id === task.project_id);
    const row = [
      task.id,
      project?.name || '',
      task.name,
      task.start_date.toISOString().split('T')[0],
      task.end_date.toISOString().split('T')[0],
      task.duration,
      task.progress,
      task.status,
      task.priority
    ].join(',');
    
    csvData.push(row);
  });
  
  // Create blob and download
  const blob = new Blob([csvData.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `gantt-tasks-${Date.now()}.csv`;
  link.click();
};

// Export to PNG image
export const exportGanttToImage = async (): Promise<void> => {
  try {
    const ganttElement = document.querySelector('.gantt-timeline');
    if (!ganttElement) {
      throw new Error('Gantt chart element not found');
    }

    const canvas = await html2canvas(ganttElement as HTMLElement, {
      backgroundColor: '#18181b',
      scale: 2
    });

    // Convert to blob and download
    canvas.toBlob((blob) => {
      if (blob) {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `gantt-chart-${Date.now()}.png`;
        link.click();
      }
    });
  } catch (error) {
    console.error('Error exporting to image:', error);
    throw error;
  }
};